import { type SpendLimitPeriodStatus } from '../models/spendLimit';

/**
 * Calculates the amount used before the current request.
 * For non-request periods (day, week, month, year), the API's usedAmount includes the request amount,
 * so we subtract it to get the usage before this request.
 *
 * @param status - The spend limit status
 * @param amount - The amount of the current request
 * @returns The amount used before the current request
 */
export const calculateUsedBeforeCurrentRequest = (
  status: SpendLimitPeriodStatus,
  amount: number,
): number => {
  return status.period === 'request'
    ? status.usedAmount
    : status.usedAmount - amount;
};

/**
 * Calculates the maximum amourt that can be reimbursed while staying within all spend limits.
 *
 * @param exceededStatuses - Array of spend limit statuses that are exceeded
 * @param amount - The amount of the current request
 * @returns The maximum reimbursable amount, or 0 if no limits are exceeded
 */
export const calculateMaxReimbursableAmount = (
  exceededStatuses: SpendLimitPeriodStatus[],
  amount: number,
): number => {
  if (exceededStatuses.length === 0) {
    return 0;
  }

  return Math.min(
    ...exceededStatuses.map((status) => {
      const usedBeforeCurrentRequest = calculateUsedBeforeCurrentRequest(
        status,
        amount,
      );
      const remainingAllowance = status.limitAmount - usedBeforeCurrentRequest;
      return Math.max(0, remainingAllowance);
    }),
  );
};
